import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import trackingService from '../services/trackingService';

const STORAGE_KEY = 'trakna_ref';
const ATTRIBUTION_STORAGE_KEY = 'trakna_attribution';
const SESSION_KEY = 'trakna_visit';
const FUNNEL_SESSION_KEY = 'trakna_rencontre_session';
const VISIT_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const ATTRIBUTION_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'fbclid',
    'gclid'
];

// Pages à ne pas tracker
const EXCLUDED_PATHS = [
    '/privacy',           // Politique de confidentialité
    '/terms',            // Conditions d'utilisation
    '/settings',         // Paramètres utilisateur
    '/admin',            // Pages admin
    '/chat/',            // Pages de chat (utilise startsWith)
    '/profile',            // Pages de chat (utilise startsWith)
    '/messages/',        // Pages de messages
    '/payment/success',  // Retour paiement, ne doit pas écraser le ref publicitaire
];

const getCookie = (name) => document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]+)`))?.[1];

const setCookie = (name, value, maxAgeSeconds = 90 * 24 * 60 * 60) => {
    document.cookie = `${name}=${value}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`;
};

const buildFbcFromFbclid = (fbclid) => {
    if (!fbclid) return undefined;
    return `fb.1.${Math.floor(Date.now() / 1000)}.${fbclid}`;
};

const getStoredAttribution = () => {
    try {
        return JSON.parse(localStorage.getItem(ATTRIBUTION_STORAGE_KEY) || '{}');
    } catch {
        return {};
    }
};

const getFacebookCookies = () => ({
    fbp: getCookie('_fbp'),
    fbc: getCookie('_fbc') || buildFbcFromFbclid(getStoredAttribution().fbclid),
});

const useTracking = ({ autoTrack = false } = {}) => {
    const location = useLocation();
    const userUid = useSelector(state => state.auth.uid);
    const [currentRef, setCurrentRef] = useState(null);

    const getLastRef = () => {
        return localStorage.getItem(STORAGE_KEY);
    };

    const getAttributionData = () => {
        const storedAttribution = getStoredAttribution();
        const { fbp, fbc } = getFacebookCookies();
        return {
            ...storedAttribution,
            ref: currentRef || getLastRef(),
            fbp,
            fbc
        };
    };

    const getFunnelSessionId = () => {
        let sessionId = localStorage.getItem(FUNNEL_SESSION_KEY);
        if (!sessionId) {
            sessionId = typeof crypto !== 'undefined' && crypto.randomUUID
                ? crypto.randomUUID().replace(/-/g, '')
                : `${Date.now()}_${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
            localStorage.setItem(FUNNEL_SESSION_KEY, sessionId);
        }
        return sessionId;
    };

    const trackFunnelEvent = (eventName, step, data = {}) => {
        const attribution = getAttributionData();
        // fbp/fbc servent à Meta, mais ne sont pas nécessaires aux statistiques internes.
        delete attribution.fbp;
        delete attribution.fbc;
        return trackingService.trackFunnelEvent({
            eventName,
            step,
            sessionId: getFunnelSessionId(),
            path: location.pathname,
            userUid,
            timestamp: new Date().toISOString(),
            attribution,
            ...data
        });
    };

    const shouldTrackVisit = (path) => {
        // Vérifier si la page est exclue
        const isExcluded = EXCLUDED_PATHS.some(excludedPath => {
            // Si le chemin exclu se termine par '/', on utilise startsWith
            if (excludedPath.endsWith('/')) {
                return path.startsWith(excludedPath);
            }
            // Sinon, on vérifie l'égalité exacte
            return path === excludedPath;
        });

        if (isExcluded) {
            return false;
        }

        const lastVisit = sessionStorage.getItem(SESSION_KEY);
        if (!lastVisit) return true;

        const visitData = JSON.parse(lastVisit);
        const timeSinceLastVisit = Date.now() - visitData.timestamp;
   
        
        // Si plus de 30 minutes ou page différente, on track
        return timeSinceLastVisit > VISIT_TIMEOUT || visitData.path !== path;
    };

    const trackAction = async (action,data) => {
        try {
            await trackingService.trackAction(action,{
                ref: currentRef || getLastRef(),
                timestamp: new Date().toISOString(),
                ...data
            });
        } catch (error) {
            console.log(error);
        }
    }

    const trackVisit = async (ref) => {
        const currentPath = location.pathname;
        
        if (!shouldTrackVisit(currentPath)) {
            return;
        }

        try {
            // Sauvegarder la visite dans sessionStorage avec le chemin
            sessionStorage.setItem(SESSION_KEY, JSON.stringify({
                timestamp: Date.now(),
                path: currentPath
            }));

            // Si nouvelle ref, mettre à jour localStorage
            if (ref) {
                const lastRef = getLastRef();
                if (lastRef !== ref) {
                    localStorage.setItem(STORAGE_KEY, ref);
                    setCurrentRef(ref);
                }
            }

            const { fbp, fbc } = getFacebookCookies();
            const attribution = getAttributionData();

            // Envoyer au serveur (avec fbp/fbc pour Meta CAPI)
            await trackingService.trackVisit({
                ref: ref || getLastRef(),
                timestamp: new Date().toISOString(),
                path: currentPath,
                fbp,
                fbc,
                userUid,
                ...attribution,
            });
        } catch (error) {
            console.error('Error tracking visit:', error);
        }
    };

    useEffect(() => {
        if (!autoTrack) return;

        // Extraire ref des paramètres d'URL
        const params = new URLSearchParams(location.search);
        const ref = params.get('ref');
        const attribution = {};
        ATTRIBUTION_KEYS.forEach((key) => {
            const value = params.get(key);
            if (value) attribution[key] = value;
        });

        if (Object.keys(attribution).length > 0) {
            const nextAttribution = {
                ...getStoredAttribution(),
                ...attribution,
                landingPath: location.pathname,
                capturedAt: new Date().toISOString()
            };
            localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(nextAttribution));

            if (attribution.fbclid && !getCookie('_fbc')) {
                setCookie('_fbc', buildFbcFromFbclid(attribution.fbclid));
            }
        }
        
        if (ref) {
            trackVisit(ref);
        } else {
            // Vérifier s'il y a une ref précédente
            const lastRef = getLastRef();
            if (lastRef) {
                trackVisit(lastRef);
            }else{
                trackVisit('NOREF');
            }
        }
  // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoTrack, location.pathname, location.search]); // Se déclenche quand l'URL ou le chemin change

    return {
        trackSignup: (userId) => trackingService.trackSignup({userId,ref:currentRef || getLastRef()}),
        trackLike: (targetUserId) => trackingService.trackLike({targetUserId,ref:currentRef || getLastRef()}),
        trackMatch: (matchedUserId) => trackingService.trackMatch({matchedUserId,ref:currentRef || getLastRef()}),
        trackMessage: (conversationId) => trackingService.trackMessage({conversationId,ref:currentRef || getLastRef()}),
        currentRef: currentRef || getLastRef(),
        getLastRef,
        getAttributionData,
        getFacebookCookies,
        trackVisit,
        trackAction,
        trackFunnelEvent
    };
};

export default useTracking;
